import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import PlateSearchConfigurator, { type SearchData } from '@/components/plate-search/PlateSearchConfigurator'
import AvailabilityResults, { type AvailablePlate } from '@/components/plate-search/AvailabilityResults'
import { searchPlates } from '@/services/search/plateSearchService'
import { findByRegionCode, type Zulassungsstelle } from '@/services/zulassungsstellenService'

export default function Wunschkennzeichen() {
    const [isSearching, setIsSearching] = useState(false)
    const [searchedPattern, setSearchedPattern] = useState('')
    const [availablePlates, setAvailablePlates] = useState<AvailablePlate[]>([])
    const [error, setError] = useState<string | null>(null)
    const [selectedOffice, setSelectedOffice] = useState<Zulassungsstelle | null>(null)
    const [lastSearchData, setLastSearchData] = useState<SearchData | null>(null)

    // Zulassungsstelle selection modal
    const [officeOptions, setOfficeOptions] = useState<Zulassungsstelle[]>([])
    const [showOfficeModal, setShowOfficeModal] = useState(false)
    const [pendingSearch, setPendingSearch] = useState<SearchData | null>(null)

    const executeSearch = useCallback(async (data: SearchData, officeId: number) => {
        setIsSearching(true)
        setError(null)
        setSearchedPattern(`${data.cityCode} ${data.letters} ${data.numbers}`)
        setAvailablePlates([])
        setLastSearchData(data)

        try {
            const results = await searchPlates({
                cityCode: data.cityCode,
                letters: data.letters || '?',
                numbers: data.numbers || '?',
                vehicleCategory: data.vehicleCategory,
                plateSize: data.plateSize,
                plateType: data.plateType,
                registrationOfficeServiceId: officeId,
                seasonFrom: data.seasonFrom,
                seasonTo: data.seasonTo,
            })

            setAvailablePlates(results.map(r => ({
                city: r.cityCode,
                middle: r.letters,
                end: r.numbers,
            })))
        } catch (err: any) {
            console.error('Search failed:', err)
            setError(err.message || 'Ein unbekannter Fehler ist aufgetreten')
        } finally {
            setIsSearching(false)
        }
    }, [])

    const handleSearch = useCallback(async (data: SearchData) => {
        // If we already have a selected office matching this city code, use it
        if (selectedOffice && selectedOffice.regionCode.toUpperCase() === data.cityCode.toUpperCase()) {
            executeSearch(data, selectedOffice.id)
            return
        }

        // Look up Zulassungsstellen for this city code
        try {
            const offices = await findByRegionCode(data.cityCode)

            if (offices.length === 0) {
                setSearchedPattern(`${data.cityCode} ${data.letters} ${data.numbers}`)
                setError(`Keine Zulassungsstelle für den Stadtcode "${data.cityCode}" gefunden. Bitte prüfen Sie den Stadtcode.`)
                return
            }

            if (offices.length === 1) {
                // Single match - use directly
                setSelectedOffice(offices[0])
                executeSearch(data, offices[0].id)
            } else {
                // Multiple matches - ask user to pick
                setOfficeOptions(offices)
                setPendingSearch(data)
                setShowOfficeModal(true)
            }
        } catch (err: any) {
            setError('Fehler beim Laden der Zulassungsstellen: ' + (err.message || ''))
        }
    }, [selectedOffice, executeSearch])

    const handleOfficeSelect = (office: Zulassungsstelle) => {
        setSelectedOffice(office)
        setShowOfficeModal(false)
        if (pendingSearch) {
            executeSearch(pendingSearch, office.id)
            setPendingSearch(null)
        }
    }

    const handleClear = () => {
        setSearchedPattern('')
        setAvailablePlates([])
        setError(null)
    }

    return (
        <div className="min-h-screen bg-dark-950 text-white relative flex flex-col pt-10 sm:pt-16 pb-20">

            {/* Background Decor */}
            <div className="absolute top-0 inset-x-0 h-[50vh] bg-gradient-to-b from-primary-900/20 via-dark-950 to-dark-950 pointer-events-none -z-10"></div>

            <div className="container-custom px-4 sm:px-6 lg:px-8 max-w-5xl z-10 mx-auto w-full">

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-10"
                >
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white via-primary-200 to-white">
                        Wunschkennzeichen<br className="sm:hidden" /> online reservieren
                    </h1>
                    <p className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto">
                        {selectedOffice
                            ? `Recherche bei ${selectedOffice.office} (${selectedOffice.bundesland})`
                            : 'Geben Sie Ihren Stadtcode ein und prüfen Sie die Verfügbarkeit'
                        }
                    </p>
                </motion.div>

                {/* Search Configurator Component */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <PlateSearchConfigurator onSearch={handleSearch} isSearching={isSearching} />
                </motion.div>

                {/* Results Component */}
                {(searchedPattern || isSearching || error) && (
                    <AvailabilityResults
                        isLoading={isSearching}
                        searchedPattern={searchedPattern}
                        availablePlates={availablePlates}
                        error={error}
                        onClear={handleClear}
                        plateSize={lastSearchData?.plateSize}
                        plateType={lastSearchData?.plateType}
                        vehicleCategory={lastSearchData?.vehicleCategory}
                        seasonFrom={lastSearchData?.seasonFrom}
                        seasonTo={lastSearchData?.seasonTo}
                    />
                )}

            </div>

            {/* Zulassungsstelle Selection Modal */}
            {showOfficeModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-dark-900 border border-dark-700 rounded-2xl p-6 max-w-lg w-full shadow-2xl"
                    >
                        <h3 className="text-xl font-bold text-white mb-2">Zulassungsstelle wählen</h3>
                        <p className="text-gray-400 text-sm mb-6">
                            Für den Stadtcode <strong className="text-white">{pendingSearch?.cityCode}</strong> gibt es mehrere Zulassungsstellen:
                        </p>
                        <div className="space-y-3 max-h-80 overflow-y-auto">
                            {officeOptions.map((office) => (
                                <button
                                    key={office.id}
                                    onClick={() => handleOfficeSelect(office)}
                                    className="w-full text-left p-4 bg-dark-800/60 hover:bg-dark-800 border border-dark-700/50 hover:border-primary-500/50 rounded-xl transition-all"
                                >
                                    <div className="font-semibold text-white">{office.name}</div>
                                    <div className="text-sm text-gray-400">{office.office} - {office.bundesland}</div>
                                    {office.reservationFee > 0 && (
                                        <div className="text-xs text-amber-400 mt-1">Reservierungsgebühr: {office.reservationFee.toFixed(2)} EUR</div>
                                    )}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => { setShowOfficeModal(false); setPendingSearch(null) }}
                            className="w-full mt-4 py-3 text-gray-400 hover:text-white text-sm transition-colors"
                        >
                            Abbrechen
                        </button>
                    </motion.div>
                </div>
            )}
        </div>
    )
}
